/**
 * Utilities for generating mesh geometry from triangle soup.
 * @module
 */
import { DIST_EPSILON } from "geom/core";
import type { Mesh3 } from "geom/mesh";
import { mesh3New } from "geom/mesh";
import { vec3Dist, vec3TriangleNormal } from "geom/vec3";
import type { UVFace } from "./discretizationTypes";
import { mergeCoplanarMeshFaces } from "./facePostProcessing";

export interface GeoBuilder {
  vertices: number[];
  uvs: number[];
  indices: number[];
  newIndices: number[];
  indicesToTransform: { endIndex: number; face: UVFace }[];
  normals?: number[];
}

export function geoBuilderNew(vertices: ArrayLike<number> = [], indices: ArrayLike<number> = []): GeoBuilder {
  return {
    vertices: vertices ? Array.from(vertices) : [],
    uvs: [],
    indices: indices ? Array.from(indices) : [],
    newIndices: [],
    indicesToTransform: [],
  };
}

export function geoBuilderAppend(target: GeoBuilder, source: GeoBuilder) {
  if (source.newIndices.length > 0) {
    const indexOffset = target.vertices.length / 3;
    const indicesStart = target.newIndices.length;
    target.newIndices.push(...source.newIndices);
    for (let i = indicesStart, ic = target.newIndices.length; i < ic; i++) {
      target.newIndices[i] += indexOffset;
    }
    target.vertices.push(...source.vertices);
    target.uvs.push(...source.uvs);
  }
}

export function geoBuilderIsEmpty(geo: GeoBuilder) {
  // GeoBuilder is non-empty if exactly one index set is non-empty.
  return geo.indices.length > 0 === geo.newIndices.length > 0;
}

export function compactGeoBuilder(geo: GeoBuilder, generateMissingNormals: boolean = false) {
  let indices;
  if (geo.indices.length > 0 && geo.newIndices.length > 0) {
    console.error("We only support one GeoBuilder index set");
    return;
  } else if (geo.newIndices.length > 0) {
    indices = geo.newIndices;
  } else if (geo.indices.length > 0) {
    indices = geo.indices;
  } else {
    console.error("Empty GeoBuilder", geo);
    return;
  }

  const { vertices, uvs } = geo;
  let normals: number[] | undefined = undefined;
  const transferUvs: boolean = geo.vertices.length / 3 === geo.uvs.length / 2;

  const REMOVED_INDEX = -1;

  // Generate normals with unique (vertex, normal) pairs
  //
  // This may add/remove indices, which is why we need to do this step first.
  if (generateMissingNormals) {
    interface VertexNormalInformation {
      triangleIndices: number[];
      nX: number;
      nY: number;
      nZ: number;
      u: number | undefined;
      v: number | undefined;
    }

    const vertexNormals: { [id: number]: Array<VertexNormalInformation> } = {};
    const addVertexNormal = (
      vertexIndex: number,
      triangleIndex: number,
      nX: number,
      nY: number,
      nZ: number,
      u: number | undefined,
      v: number | undefined,
    ) => {
      if (vertexNormals[vertexIndex]) {
        for (const vn of vertexNormals[vertexIndex]) {
          if (vec3Dist(nX, nY, nZ, vn.nX, vn.nY, vn.nZ) < DIST_EPSILON) {
            vn.triangleIndices.push(triangleIndex);
            return;
          }
        }
      } else {
        vertexNormals[vertexIndex] = [];
      }
      vertexNormals[vertexIndex].push({ triangleIndices: [triangleIndex], nX, nY, nZ, u, v });
    };

    const triangleCount = indices.length / 3;
    for (let i = 0; i < triangleCount; i++) {
      const triangleIndex = 3 * i;
      const aIndex = indices[triangleIndex];
      const bIndex = indices[triangleIndex + 1];
      const cIndex = indices[triangleIndex + 2];

      const [nX, nY, nZ] = vec3TriangleNormal(
        vertices[3 * aIndex],
        vertices[3 * aIndex + 1],
        vertices[3 * aIndex + 2],
        vertices[3 * bIndex],
        vertices[3 * bIndex + 1],
        vertices[3 * bIndex + 2],
        vertices[3 * cIndex],
        vertices[3 * cIndex + 1],
        vertices[3 * cIndex + 2],
      );

      if (!isNaN(nX) && !isNaN(nY) && !isNaN(nZ)) {
        let u, v;

        if (transferUvs) {
          u = uvs[2 * aIndex];
          v = uvs[2 * aIndex + 1];
        }
        addVertexNormal(aIndex, triangleIndex, nX, nY, nZ, u, v);

        if (transferUvs) {
          u = uvs[2 * bIndex];
          v = uvs[2 * bIndex + 1];
        }
        addVertexNormal(bIndex, triangleIndex + 1, nX, nY, nZ, u, v);

        if (transferUvs) {
          u = uvs[2 * cIndex];
          v = uvs[2 * cIndex + 1];
        }
        addVertexNormal(cIndex, triangleIndex + 2, nX, nY, nZ, u, v);
      }
    }

    let nextVertexId = vertices.length / 3;
    geo.normals = normals = new Array<number>(3 * nextVertexId);

    for (const key of Object.keys(vertexNormals)) {
      const vertexId = parseInt(key);

      const sortedVertexNormals: VertexNormalInformation[] = vertexNormals[vertexId].sort(
        (a, b) => a.triangleIndices.length - b.triangleIndices.length,
      );

      // Most frequent vertex normal is written for that vertexId and doesn't require updating triangle indices
      const mostFrequent = sortedVertexNormals.pop();
      if (!mostFrequent) {
        continue;
      }
      normals[3 * vertexId] = mostFrequent.nX;
      normals[3 * vertexId + 1] = mostFrequent.nY;
      normals[3 * vertexId + 2] = mostFrequent.nZ;
      if (transferUvs) {
        geo.uvs[2 * vertexId] = mostFrequent.u ?? 0;
        geo.uvs[2 * vertexId + 1] = mostFrequent.v ?? 0;
      }

      // Remaining vertex normals are manually added and triangle indices update
      sortedVertexNormals.forEach((vertexNormal) => {
        vertices.push(vertices[3 * vertexId], vertices[3 * vertexId + 1], vertices[3 * vertexId + 2]);
        if (normals) {
          normals.push(vertexNormal.nX, vertexNormal.nY, vertexNormal.nZ);
        }
        if (transferUvs && vertexNormal.u !== undefined && vertexNormal.v !== undefined) {
          geo.uvs.push(vertexNormal.u, vertexNormal.v);
        }
        vertexNormal.triangleIndices.forEach((i) => {
          indices[i] = nextVertexId;
        });
        ++nextVertexId;
      });
    }
  }

  // Compact indices
  const newIndices = indices.filter((x) => x !== REMOVED_INDEX);
  const newUniqueIndices = new Set<number>(newIndices);
  const sortedFinalIndices = Array.from(newUniqueIndices).sort((a, b) => a - b);

  const indexMapping = {};
  sortedFinalIndices.forEach((oldIndex, newIndex) => {
    indexMapping[oldIndex] = newIndex;
  });

  for (let i = 0; i < indices.length; i++) {
    indices[i] = indexMapping[indices[i]];
  }

  // Compact buffers
  geo.vertices = vertices.filter((_, i) => newUniqueIndices.has(Math.floor(i / 3)));
  if (normals) {
    geo.normals = normals.filter((_, i) => newUniqueIndices.has(Math.floor(i / 3)));
  }
  if (transferUvs) {
    geo.uvs = geo.uvs.filter((_, i) => newUniqueIndices.has(Math.floor(i / 2)));
  } else {
    geo.uvs = [];
  }
}

export function compactMesh3FromGeoBuilder(
  geo: GeoBuilder,
  {
    mergeCoplanarFaces = false,
    generateMissingNormals = false,
  }: { mergeCoplanarFaces: boolean; generateMissingNormals: boolean },
): Mesh3 {
  compactGeoBuilder(geo, generateMissingNormals);

  let result = mesh3New(
    new Float32Array(geo.vertices),
    new Uint32Array(geo.newIndices),
    geo.normals ? new Float32Array(geo.normals) : undefined,
    geo.uvs ? new Float32Array(geo.uvs) : undefined,
  );

  if (mergeCoplanarFaces) {
    result = mergeCoplanarMeshFaces({ mesh: result });
  }
  return result;
}
